import type { BridgeEventType } from '@/types/webhook';

// Data for the Webhooks guide on the API docs page (components/api-docs/WebhooksGuide.tsx).
// The "Webhooks" section of info.description in src/data/openapi.yaml is the source of truth;
// webhookGuide.test.ts checks that the snippet, headers and payload fields still appear there.

/** Copied verbatim from info.description. */
export const VERIFY_SNIPPET = `// Node: rawBody is the unparsed request body (Buffer)
const ts = req.headers['x-bridge-timestamp'] ?? '';
const want = Buffer.from('sha256=' + crypto.createHmac('sha256', secret) // 'whsec_...' as-is
  .update(ts + '.').update(rawBody).digest('hex'));
const got = Buffer.from(req.headers['x-bridge-signature'] ?? '');
const ok = got.length === want.length && crypto.timingSafeEqual(got, want)
  && Math.abs(Date.now() / 1000 - Number(ts)) <= 300;`;

export const WEBHOOK_HEADERS: readonly { name: string; meaning: string }[] = [
  { name: 'X-Bridge-Event', meaning: 'The event type.' },
  { name: 'X-Bridge-Event-Id', meaning: "The event's id (UUID), the same on every retry." },
  { name: 'X-Bridge-Delivery-Id', meaning: 'The delivery row id. A manual redeliver gets a new one.' },
  { name: 'X-Bridge-Timestamp', meaning: 'Unix seconds when this attempt was signed.' },
  { name: 'X-Bridge-Signature', meaning: 'sha256= followed by the lowercase hex HMAC-SHA256 of timestamp + "." + raw body.' },
];

export interface PayloadField {
  name: string;
  /** Optional note, e.g. the allowed values. */
  note?: string;
}

/** Fields of every app.domain_switch_* event. */
const SWITCH_FIELDS: readonly PayloadField[] = [
  { name: 'switch_id', note: 'id of the switch (GET /apps/{id}/domain-switch)' },
  { name: 'from_host' },
  { name: 'to_host' },
  { name: 'from_domain' },
  { name: 'to_domain' },
];

/** Extra `data` fields per event type, on top of the `data.app` snapshot (domain events have none). */
export const PAYLOAD_FIELDS: readonly { type: BridgeEventType; fields: readonly PayloadField[] }[] = [
  {
    type: 'app.status_changed',
    fields: [{ name: 'previous_status' }, { name: 'source', note: 'user, deploy, migration or sync' }],
  },
  { type: 'app.deployed', fields: [{ name: 'deployment_id' }] },
  {
    type: 'app.deploy_failed',
    fields: [{ name: 'deployment_id' }, { name: 'error', note: '"cancelled", "timeout", or a free-text message' }],
  },
  { type: 'app.migrated', fields: [{ name: 'from_endpoint_id' }, { name: 'to_endpoint_id' }] },
  {
    type: 'app.migration_failed',
    fields: [
      { name: 'error', note: 'free text' },
      { name: 'step', note: 'human-readable label, not an enum; only "setup" and "cutover" are stable' },
    ],
  },
  { type: 'app.domain_switch_started', fields: SWITCH_FIELDS },
  { type: 'app.domain_switched', fields: SWITCH_FIELDS },
  {
    type: 'app.domain_switch_failed',
    fields: [
      ...SWITCH_FIELDS,
      { name: 'step', note: 'setup, dns, redeploy, verify, flip or recovery' },
      { name: 'error', note: 'free text; the app is still on from_host' },
    ],
  },
  { type: 'app.deleted', fields: [] },
  {
    type: 'domain.ready_changed',
    fields: [
      { name: 'domain_id' },
      { name: 'domain' },
      { name: 'deploy_ready', note: 'new value of GET /domains/{id} deploy_ready; no data.app, app_id is null' },
    ],
  },
];

/** Values of data.app.status and data.previous_status. */
export const APP_EVENT_STATUSES: readonly string[] = [
  'pending', 'running', 'stopped', 'failed', 'healthy', 'unhealthy', 'starting', 'migrating',
];
